// World.tsx
import React, {useEffect, useRef} from 'react';
import useMap from '../hooks/useMap';
import CountryComponent from './CountryComponent';
import {twMerge} from 'tailwind-merge';
import * as d3 from 'd3';

interface WorldProps {
    className?: string;
    children?: React.ReactNode;
    infinite?: boolean; // Optional prop for infinite scrolling
}

/**
 * Helper function to perform a proper modulo operation for wrapping.
 * It ensures that the result is always between -width/2 and +width/2.
 */
const wrapX = (x: number, width: number, k: number): number => {
    const scaledWidth = width * k;
    return ((x + scaledWidth / 2) % scaledWidth + scaledWidth) % scaledWidth - scaledWidth / 2;
};

const World: React.FC<WorldProps> = ({className, children, infinite = false}) => {
    const {countries, renderedCountries} = useMap();

    // Define initial viewBox dimensions
    const viewBoxWidth = 1009.6727;
    const viewBoxHeight = 665.96301;

    // Filter countries to render
    const countriesToRender = countries.filter(country => renderedCountries.includes(country.alpha2Code));

    // Refs for SVG and group container
    const svgRef = useRef<SVGSVGElement | null>(null);
    const gContainerRef = useRef<SVGGElement | null>(null);

    // Ref to track current transform
    const currentTransform = useRef<d3.ZoomTransform>(d3.zoomIdentity);
    const isProgrammatic = useRef(false);

    useEffect(() => {
        if (!svgRef.current || !gContainerRef.current) return;

        const svg = d3.select(svgRef.current);
        const g = d3.select(gContainerRef.current);

        // Define zoom behavior with scale limits
        const zoomBehavior = d3.zoom<SVGSVGElement, unknown>()
            .scaleExtent([0.8, 1000]) // Zoom out to 0.8x, zoom in to 1000x
            .on('zoom', (event) => {
                if (isProgrammatic.current) return;

                let {x, y, k} = event.transform;

                if (infinite) {
                    const wrappedX = wrapX(x, viewBoxWidth, k);

                    if (wrappedX !== x) {
                        const newTransform = d3.zoomIdentity.translate(wrappedX, y).scale(k);
                        currentTransform.current = newTransform;

                        isProgrammatic.current = true;

                        svg.call(zoomBehavior.transform, newTransform);

                        setTimeout(() => {
                            isProgrammatic.current = false;
                        }, 0);

                        return;
                    }

                    currentTransform.current = event.transform;
                } else {
                    currentTransform.current = event.transform;
                }

                // Apply the transform
                g.attr('transform', `translate(${x}, ${y}) scale(${k})`);
            })
            .on('end', (event) => {
                if (!infinite) return;

                const t = currentTransform.current;
                let {x, y, k} = t;

                const wrappedX = wrapX(x, viewBoxWidth, k);

                if (wrappedX !== x) {
                    const newTransform = d3.zoomIdentity.translate(wrappedX, y).scale(k);
                    currentTransform.current = newTransform;

                    isProgrammatic.current = true;

                    svg.call(zoomBehavior.transform, newTransform);

                    setTimeout(() => {
                        isProgrammatic.current = false;
                    }, 0);
                }
            });

        // Apply zoom behavior to SVG
        svg.call(zoomBehavior);

        // Prevent default double-click zoom behavior
        svg.on('dblclick.zoom', null);
    }, [infinite, viewBoxWidth, viewBoxHeight]);

    // Create centralized defs for all flag patterns
    const defs = (
        <defs>
            {countriesToRender.map(country => (
                <pattern
                    key={`pattern-${country.alpha2Code}`}
                    id={`${country.alpha2Code}-flag`}
                    patternUnits="objectBoundingBox"
                    width="1"
                    height="1"
                    patternContentUnits="objectBoundingBox"
                >
                    <image
                        href={country.flagUrl}
                        x="0"
                        y="0"
                        width="1"
                        height="1"
                        preserveAspectRatio="none"
                    />
                </pattern>
            ))}
        </defs>
    );

    return (
        <div
            className={twMerge(
                'relative flex items-center justify-center w-full h-full min-w-0 min-h-0 overflow-hidden',
                className
            )}
        >
            <svg
                ref={svgRef}
                xmlns="http://www.w3.org/2000/svg"
                viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
                preserveAspectRatio="xMidYMid meet"
                className="world-map bg-transparent w-screen h-screen"
                style={{cursor: infinite ? 'grab' : 'default'}} // Change cursor if draggable
            >
                {defs}
                <g ref={gContainerRef}>
                    {/* Central <g> */}
                    <g
                        id="world-content"
                        width={viewBoxWidth}
                        height={viewBoxHeight}
                        pointerEvents="visiblePainted"
                    >
                        {countriesToRender.map((country) => (
                            <CountryComponent key={country.alpha2Code} country={country}/>
                        ))}
                        {children}
                    </g>
                    {/* Render additional copies only if infinite is true */}
                    {infinite && (
                        <>
                            {/* Left Copy */}
                            <g
                                className="world-copy"
                                transform={`translate(${-viewBoxWidth}, 0)`}
                            >
                                {countriesToRender.map((country) => (
                                    <CountryComponent key={`${country.alpha2Code}-left`} country={country}/>
                                ))}
                                {children}
                            </g>
                            {/* Right Copy */}
                            <g
                                className="world-copy"
                                transform={`translate(${viewBoxWidth}, 0)`}
                            >
                                {countriesToRender.map((country) => (
                                    <CountryComponent key={`${country.alpha2Code}-right`} country={country}/>
                                ))}
                                {children}
                            </g>
                        </>
                    )}
                </g>
            </svg>
        </div>
    );
};

export default React.memo(World);
